import { ReactNode, useEffect, useRef } from 'react';
import { useIntersection } from '@mantine/hooks';

import { LayoutProvider } from './LayoutContext';

import styles from './page.module.scss';

export type PageProps = {
  headerHeight?: number;
  pageHeaderNavigation?: ReactNode;
  pageHeader?: ReactNode;
  pageFilter?: ReactNode;
  pageContent?: ReactNode;
  hasVisiblePageFilters?: boolean;
};

export function Page({ hasVisiblePageFilters, ...pageProps }: PageProps) {
  if (hasVisiblePageFilters) {
    return <WithVisiblePageFilters {...pageProps} />;
  }
  return <WithoutVisiblePageFilters {...pageProps} />;
}

function WithVisiblePageFilters({
  headerHeight = 0,
  pageHeaderNavigation,
  pageHeader,
  pageFilter,
  pageContent,
}: PageProps) {
  const { ref: pageHeaderRef, entry: pageHeaderEntry } = useIntersection({
    threshold: 0,
    rootMargin: headerHeight ? '-28px' : '0px', // page padding, em(32)
  });

  // we need to set proper top value for sticky page filter
  const pageFilterTop = `calc(${headerHeight}px + 0.4em)`;

  const scrollTopOffset = useRef(0);

  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const filterHeight = filterRef.current?.getBoundingClientRect().height ?? 0;

    scrollTopOffset.current =
      filterHeight +
      headerHeight +
      // em($unit) if we have a filter, because it already comes with bottom spacing; em(3 * $unit) to have distance to the sub navigation equal to the horizontal padding of the content otherwise
      (filterHeight ? 7 : 21);
  });

  return (
    <LayoutProvider scrollTopOffset={scrollTopOffset}>
      <div className={`page-root ${styles['root']}`}>
        {pageHeaderNavigation && <div>{pageHeaderNavigation}</div>}
        {pageHeader && (
          <div ref={pageHeaderRef} className={styles['header']}>
            {pageHeader}
          </div>
        )}
        {pageFilter && (
          <div
            ref={filterRef}
            className={styles['filter--sticky']}
            style={{ top: pageFilterTop }}
            data-testid="page-filter-sticky"
          >
            {pageFilter}
            <div className={styles['backdrop']}></div>
            {!pageHeaderEntry?.isIntersecting && (
              <div className={styles['divider']}></div>
            )}
          </div>
        )}
        {pageContent && (
          <div className={`page-content ${styles['content']}`}>
            {pageContent}
          </div>
        )}
      </div>
    </LayoutProvider>
  );
}

function WithoutVisiblePageFilters({
  headerHeight = 0,
  pageHeaderNavigation,
  pageHeader,
  pageFilter,
  pageContent,
}: PageProps) {
  const scrollTopOffset = useRef(headerHeight + 7);

  scrollTopOffset.current = headerHeight + 7;

  return (
    <LayoutProvider scrollTopOffset={scrollTopOffset}>
      <div className={`page-root ${styles['root']}`}>
        {pageHeaderNavigation && <div>{pageHeaderNavigation}</div>}
        {pageHeader && <div className={styles['header']}>{pageHeader}</div>}
        {pageFilter && (
          // page filters still need to be rendered due to internal request logic
          <div className={styles['filter']} data-testid="page-filter">
            {pageFilter}
          </div>
        )}
        {pageContent && (
          <div className={`page-content ${styles['content']}`}>
            {pageContent}
          </div>
        )}
      </div>
    </LayoutProvider>
  );
}
